import { styled } from "@linaria/react"
import { useNavigate } from "@tanstack/react-router"
import { useState } from "react"

import { Centered } from "@/components/Centered/Centered.ts"
import { Spring, Vertical } from "@/components/Flex.ts"
import { GradientBlob } from "@/components/GradientBlob.ts"
import { useCloseLocation, useOrgHomeLocation } from "@/hooks/useMatterLocation.ts"
import { CloseLight } from "@/icons/Icons.ts"
import { pageTitle } from "@/lib/displayUtils.ts"
import { startViewTransition } from "@/lib/dom.ts"
import { InviteTeammatesDialog } from "@/routes/auth/InviteTeammatesDialog.tsx"
import { Page } from "@/routes/auth/Page.tsx"
import { HomeStyles } from "@/routes/home/HomeStyles.ts"
import { NewProjectForm } from "@/routes/projects/NewProjectForm.tsx"
import { useOrganizationPermissions } from "@/routes/projects/projectHooks.ts"


export const NewProjectScene = () => {
  const navigate = useNavigate()
  const closeLocation = useCloseLocation(useOrgHomeLocation())

  const leave = () => startViewTransition(() => navigate(closeLocation))

  const [inviteTeammatesDialogOpen, setInviteTeammatesDialogOpen] = useState(false)

  const permissions = useOrganizationPermissions()

  return (
    <Page>
      <title>{pageTitle("New project")}</title>

      <HomeStyles.PageCloseButton kind="icon" onClick={leave}>
        <CloseLight />
      </HomeStyles.PageCloseButton>

      <FormCentered vertical={false}>
        <FormTitle data-testid="new-project-title">
          Add your project
        </FormTitle>

        <LimitedWidthForm>
          <Vertical gap="24px" height="100%">
            <FormSubtitle>
              To get started, connect an existing repository.
              Matter will mirror your codebase and set it up to bring your ideas to life.
            </FormSubtitle>

            <NewProjectForm />

            <Spring />

            {permissions.canInviteUsers && (
              <Vertical alignItems="center">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    setInviteTeammatesDialogOpen(true)
                  }}
                  data-testid="invite-developer"
                >
                  Invite a teammate
                </a>
                <LinkDescription>
                  to help you set up your project
                </LinkDescription>

                <InviteTeammatesDialog
                  dialogOpen={inviteTeammatesDialogOpen}
                  setDialogOpen={setInviteTeammatesDialogOpen}
                />
              </Vertical>
            )}
          </Vertical>
        </LimitedWidthForm>
      </FormCentered>

      <GradientBlob />
    </Page>
  )
}

const FormCentered = styled(Centered)`
  margin-top: 48px;
  min-height: auto;
`

const FormTitle = styled(HomeStyles.PageSubtitle)`
  view-transition-name: new-project-h2;
`

const FormSubtitle = styled.div`
  text-align: center;
`

const LinkDescription = styled.div`
  text-align: center;
  color: var(--secondary-text-color);
`

const AnimatedContent = styled.div`
  width: 100%;

  view-transition-name: new-project-content;
`

const LimitedWidthForm = styled(AnimatedContent)`
  max-width: 480px;
  height: 100%;
`
